import {
  useImperativeHandle,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type Ref,
} from "react";

import {
  ArrowUpIcon,
  Box,
  CheckIcon,
  CloseIcon,
  Column,
  EmailIcon,
  IconButton,
  Row,
  motionDurations,
  motionEasings,
} from "@hightouch/htui";
import { AnimatePresence, motion } from "motion/react";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { Placeholder } from "@tiptap/extensions";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import type { SuggestionOptions } from "@tiptap/suggestion";

import type { MentionNames } from "@hightouch/lib/llm/asset-comments/mention-tokens";

import { buildSuggestionMenu } from "src/components/suggestion-menu/suggestion-menu";

import { bodyToEditorContent } from "./body-to-editor-content";
import { linkPastedMentions } from "./paste-mentions";
import {
  MENTION_CHAR,
  MENTION_NODE_NAME,
  MentionNode,
  toMentionOption,
} from "./mention-node";
import { markMentionPopup, trackMentionPopupState } from "./mention-popup";
import {
  MENTION_SECTIONS,
  type MentionAttrs,
  type MentionOption,
  mentionSection,
  renderMentionOption,
} from "./mention-options-list";

export type CommentComposerHandle = {
  clear: () => void;
  focus: () => void;
};

export type CommentDraft = {
  body: string;
  mentions: MentionOption[];
};

/** Rewriting a comment that already exists, rather than writing a new one. */
export type CommentEdit = {
  /** The stored body, mention tokens and all. */
  initialBody: string;
  mentionNames: MentionNames;
  onCancel: () => void;
};

type Props = {
  placeholder: string;
  /** Resolves the `@`-query to selectable people and agents. */
  searchMentions: (query: string) => Promise<MentionOption[]>;
  onSubmit: (draft: CommentDraft) => void;
  isSubmitting?: boolean;
  autoFocus?: boolean;
  borderRadius?: "xl" | "4xl";
  handleRef?: Ref<CommentComposerHandle>;
  /** Set when the box is rewriting an existing comment rather than composing one. */
  edit?: CommentEdit;
  /** Beside the mention button, such as a choice about what sending also does. Keeps that row out before anything is typed. */
  option?: ReactNode;
};

/**
 * Single-line-feel comment box with `@`-mention autocomplete.
 *
 * Mentions serialize into the body as `@[Name](user:4821)`, so the text
 * identifies who it names. The same targets are sent alongside as the mention
 * list; the service rejects the pair if they disagree, which is what keeps a
 * composer bug from writing notification rows the body doesn't support.
 */
export const CommentComposer = ({
  placeholder,
  searchMentions,
  onSubmit,
  isSubmitting = false,
  autoFocus = false,
  borderRadius = "4xl",
  handleRef,
  edit,
  option,
}: Props) => {
  // The editor reads this on every query, so a ref keeps the extension stable
  // across renders instead of rebuilding it whenever the callback identity
  // changes.
  const searchRef = useRef(searchMentions);
  searchRef.current = searchMentions;

  // Armed with the composer rather than with the popup: the recording has to be
  // in place before the keydown that dismisses it.
  trackMentionPopupState();

  /**
   * Whether the `@`-suggestion popup is showing.
   *
   * ProseMirror runs `editorProps.handleKeyDown` before any plugin's, so the
   * composer sees Enter first and would submit while the user is only trying to
   * pick a mention. Tracking the popup here lets the composer stand down and
   * leave Enter to the suggestion plugin.
   */
  const isMentionOpenRef = useRef(false);

  /**
   * Whether the box holds something worth sending. This both expands the box —
   * the composer rests as a single line and grows a row carrying the mention
   * shortcut once there's a sentence to put a name in, so the resting state
   * stays out of the way of the ad underneath — and arms the send button.
   *
   * Read off the body `submit` would store, so the button is live exactly when
   * pressing it would do something: whitespace alone is not a comment.
   */
  const [hasBody, setHasBody] = useState(
    () => (edit?.initialBody ?? "").trim().length > 0,
  );

  // Built once: the comment underneath keeps polling, and rebuilding from a
  // later render's body would drop whatever has been typed since.
  const [initialContent] = useState(() =>
    edit ? bodyToEditorContent(edit.initialBody, edit.mentionNames) : undefined,
  );

  const editor = useEditor({
    content: initialContent,
    extensions: [
      Document,
      Paragraph,
      Text,
      // Gives Shift+Enter a node to insert. Without it the schema has no way to
      // represent a line break, and the composer's own keydown handler — which
      // already declines to submit on Shift+Enter — falls through to nothing.
      HardBreak,
      Placeholder.configure({ placeholder }),
      MentionNode.configure({
        suggestion: buildMentionSuggestion({
          onSearch: (query) => searchRef.current(query),
          onOpenChange: (isOpen) => {
            isMentionOpenRef.current = isOpen;
          },
        }),
      }),
    ],
    // `"end"` rather than `true`: the caret belongs after the text being
    // rewritten, not before it.
    autofocus: edit ? "end" : autoFocus,
    onUpdate: ({ editor: updated }) =>
      setHasBody(draftBody(updated).length > 0),
    editorProps: {
      // A contenteditable carries no implicit role, and the placeholder is a
      // CSS pseudo-element a screen reader never sees.
      attributes: {
        class: "asset-comment-composer",
        role: "textbox",
        "aria-label": edit ? "Edit comment" : placeholder,
        "aria-multiline": "true",
      },
      handlePaste: (view) => {
        const from = view.state.selection.from;
        // After the default paste lands, the selection marks where it ended.
        setTimeout(() => {
          if (view.isDestroyed) return;
          void linkPastedMentions(
            view,
            { from, to: view.state.selection.to },
            searchRef.current,
          );
        });
        return false;
      },
      handleKeyDown: (_view, event) => {
        // Let the mention popup own Enter and Escape while it's open: Enter
        // picks the highlighted person, Escape dismisses just the popup.
        if (isMentionOpenRef.current) {
          return false;
        }

        // Enter submits; Shift+Enter is a newline.
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          submit();
          return true;
        }

        return false;
      },
    },
  });

  const submit = () => {
    if (!editor) {
      return;
    }

    const body = draftBody(editor);

    if (body.length === 0 || isSubmitting) {
      return;
    }

    onSubmit({ body, mentions: collectMentions(editor) });

    // Editing, the box *is* the comment: emptying it would blank the text mid-save
    // and leave nothing to fall back to if the save fails. The surface closes it.
    if (!edit) {
      clear();
    }
  };

  const clear = () => {
    editor?.commands.clearContent();
    setHasBody(false);
  };

  /**
   * The `@` button types the character rather than opening a picker of its own,
   * so the popup it triggers is the same one typing `@` gives you — one
   * mention flow, one code path.
   *
   * The suggestion plugin only fires at a word boundary, so a `@` landing
   * straight after a word gets a space first — otherwise the button would
   * insert a character that does nothing. Only text needs the check: after a
   * mention the `@` starts a text node of its own, whose empty prefix is
   * already a boundary the plugin accepts.
   */
  const insertMentionChar = () => {
    if (!editor) {
      return;
    }

    const { $from } = editor.state.selection;
    const charBefore = $from.nodeBefore?.isText
      ? ($from.nodeBefore.text?.slice(-1) ?? "")
      : "";
    const needsSpace = charBefore !== "" && charBefore !== " ";

    editor
      .chain()
      .focus()
      .insertContent(`${needsSpace ? " " : ""}${MENTION_CHAR}`)
      .run();
  };

  /**
   * The whole box is the field, not just the line of text in it: a press on the
   * padding around the editor, or on the empty stretch of the action row, puts
   * the caret in at the end. Buttons and the editor answer their own presses.
   *
   * The default is prevented so the press doesn't take focus back off the
   * editor the moment it's handed over.
   */
  const focusOnBoxPress = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest("button, label, .ProseMirror")
    ) {
      return;
    }

    event.preventDefault();
    editor?.commands.focus("end");
  };

  useImperativeHandle(handleRef, () => ({
    clear,
    focus: () => editor?.commands.focus(),
  }));

  // Editing keeps the row out whatever the box contains: emptying the text is no
  // reason to take the discard action away.
  const isActionRowOpen = Boolean(edit) || hasBody || Boolean(option);

  const canSubmit = hasBody && !isSubmitting;

  return (
    <Column
      gap={2}
      p={2.5}
      bg="base.lightBackground"
      borderWidth="hairline"
      borderStyle="solid"
      borderColor="base.border"
      borderRadius={borderRadius}
      cursor="text"
      // `cursor` inherits, and the buttons inside the box are still buttons.
      sx={{ button: { cursor: "pointer" } }}
      _focusWithin={{ borderColor: "primary.border", boxShadow: "outline" }}
      transitionProperty="border-color, box-shadow"
      transitionDuration="fast"
      transitionTimingFunction="easeOutQuad"
      onMouseDown={focusOnBoxPress}
    >
      {/* Collapsed, the send button sits beside the text; expanded, it moves
          down to the action row. Either way there's a visible way to send —
          resting it says where send is, and says there's nothing to send yet. */}
      <Row align="flex-end" gap={1}>
        <Box
          flex={1}
          minWidth={0}
          fontSize="md"
          lineHeight="5"
          color="text.primary"
          sx={{
            ".ProseMirror": { outline: "none", minHeight: "20px" },
            // The mention renders as a plain inline span, so it flows inside
            // the sentence instead of breaking onto its own line.
            [`.ProseMirror span[data-type="${MENTION_NODE_NAME}"]`]: {
              display: "inline",
              color: "text.accent",
              fontWeight: "medium",
            },
            // TipTap's placeholder extension exposes the empty state only as a
            // CSS pseudo-element; there's no prop for it.
            ".ProseMirror p.is-editor-empty:first-of-type::before": {
              content: "attr(data-placeholder)",
              color: "text.placeholder",
              float: "left",
              height: 0,
              pointerEvents: "none",
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>

        {!isActionRowOpen && (
          <SendButton isDisabled={!canSubmit} onClick={submit} />
        )}
      </Row>

      {/* The mention shortcut only earns its space once there's a sentence to
          put a name in; the row grows in rather than popping. `initial={false}`
          keeps it from animating in when editing opens with it already out. */}
      <AnimatePresence initial={false}>
        {isActionRowOpen && (
          // Plain `motion.div` rather than htui's `MotionBox`: Chakra's own
          // `transition` style prop shadows motion's on the wrapped component,
          // and this row needs no theme tokens.
          <motion.div
            style={{ overflow: "hidden" }}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              duration: motionDurations.fast,
              ease: motionEasings.easeOutQuad,
            }}
          >
            <Row align="center" justify="space-between" gap={2}>
              <Row align="center" gap={2} minWidth={0}>
                <IconButton
                  aria-label="Mention someone"
                  icon={EmailIcon}
                  size="xs"
                  shape="circle"
                  variant="tertiary"
                  onClick={insertMentionChar}
                />
                {option}
              </Row>

              {edit ? (
                <Row align="center" gap={1}>
                  <IconButton
                    aria-label="Discard changes"
                    icon={CloseIcon}
                    size="xs"
                    shape="circle"
                    variant="tertiary"
                    isDisabled={isSubmitting}
                    onClick={edit.onCancel}
                  />

                  <IconButton
                    aria-label="Save changes"
                    icon={CheckIcon}
                    size="xs"
                    shape="circle"
                    variant="primary"
                    isDisabled={!canSubmit}
                    onClick={submit}
                  />
                </Row>
              ) : (
                <SendButton isDisabled={!canSubmit} onClick={submit} />
              )}
            </Row>
          </motion.div>
        )}
      </AnimatePresence>
    </Column>
  );
};

const SendButton = ({
  isDisabled,
  onClick,
}: {
  isDisabled: boolean;
  onClick: () => void;
}) => (
  <IconButton
    aria-label="Send comment"
    icon={ArrowUpIcon}
    size="xs"
    shape="circle"
    variant="primary"
    isDisabled={isDisabled}
    onClick={onClick}
  />
);

/**
 * The body as it would be stored, which is also what decides whether there's
 * anything to send.
 *
 * One newline per break. The default block separator is a blank line, which
 * would make a paragraph break render as a wider gap than the line break
 * Shift+Enter produces — the two read identically in the composer, so they
 * should store identically too.
 */
function draftBody(editor: Editor): string {
  return editor.getText({ blockSeparator: "\n" }).trim();
}

/**
 * Walks the document for mention nodes rather than trusting everything the user
 * ever picked: a mention typed then deleted must not still notify that person.
 *
 * Built from the node's own attributes, since the body's token is too. Reading
 * from the picked-this-session map instead would drop any mention that got into
 * the document another way — pasted, or restored with a draft — while
 * `renderText` still wrote its token, and the service rejects that pair.
 *
 * Keyed by kind and id together, matching the body token — a user and an agent
 * can hold the same id text, and collapsing them would drop one of the two.
 */
function collectMentions(editor: Editor): MentionOption[] {
  const present = new Map<string, MentionOption>();

  editor.state.doc.descendants((node) => {
    if (node.type.name !== MENTION_NODE_NAME) {
      return;
    }

    const option = toMentionOption(node.attrs);

    if (option) {
      present.set(optionKey(option.kind, option.value), option);
    }
  });

  return [...present.values()];
}

/** Keys a picked option the same way the document node is looked up. */
function optionKey(kind: string, id: string): string {
  return `${kind}:${id}`;
}

function buildMentionSuggestion({
  onSearch,
  onOpenChange,
}: {
  onSearch: (query: string) => Promise<MentionOption[]>;
  onOpenChange: (isOpen: boolean) => void;
}): Omit<SuggestionOptions<MentionOption, MentionAttrs>, "editor"> {
  return {
    char: MENTION_CHAR,
    // Uncapped: the search bounds each kind on its own — members server-side,
    // agents by there only ever being a handful. A cap across the combined list
    // would let agents, which sort first, evict the members below them.
    items: ({ query }) => onSearch(query),
    ...buildSuggestionMenu<MentionOption, MentionAttrs>({
      getKey: (option) => `${option.kind}:${option.value}`,
      getSection: mentionSection,
      sectionOrder: MENTION_SECTIONS,
      // Only worth a heading once there are two kinds to tell apart.
      hideLoneSection: true,
      renderItem: renderMentionOption,
      listLabel: "People and agents",
      toAttrs: (option) => ({
        id: option.value,
        kind: option.kind,
        label: option.label,
      }),
      placement: "top-start",
      onOpenChange,
      // Marks this popup as the mention list for outer Escape handlers.
      onPopupCreate: markMentionPopup,
    }),
  };
}
